/*
 * eu-seller/rules.js — what applies to ONE product in the countries it is
 * sold to, with the date each obligation started or starts.
 *
 * The answer is a checklist, not a verdict: every line names the act and
 * article, where the information goes (product, packaging, parcel, listing,
 * paperwork, registration), whether it applies now, from a fixed date, or
 * only once an implementing act exists — and which fields of the product
 * card are still empty for it. Nothing here says «compliant».
 *
 * DATES (checked against the consolidated texts, 21.10.2026):
 *   GPSR 2023/988 ............................... 13.12.2024 (Art. 52)
 *   Batteries 2023/1542: CE 18.08.2024, separate-collection symbol 18.08.2025
 *     (Art. 13(4)), QR 18.02.2027 (Art. 13(6)), removability 18.02.2027
 *     (Art. 11), labels Art. 13(1)–(3): 18.08.2026 OR 18 months after the
 *     implementing act, whichever is later — no such act in Cellar on
 *     21.10.2026, so «waits for the act».
 *   PPWR 2025/40: applies 12.08.2026 (Art. 71); Art. 15(5)–(6) identifier and
 *     manufacturer on the packaging from then; harmonised material label
 *     Art. 12(1) 12.08.2028 OR 24 months after the implementing act (none on
 *     21.10.2026); reusable label 12.02.2029.
 *   Toys: Directive 2009/48 until 31.07.2030; Regulation 2025/2509 applies
 *     01.08.2030 (Art. 59), toys placed before then stay sellable (Art. 57).
 *   Spain RD 1055/2022 art. 13 marking: 01.01.2025 (disp. final 5ª).
 *   Italy D.Lgs 152/2006 art. 219(5) (as amended by 116/2020): 01.01.2023.
 *   USB-C (Dir. 2022/2380): 28.12.2024, laptops 28.04.2026.
 *
 * Pure module: no DOM. Tested by tests/js/eu-seller-rules.test.mjs.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.EuRules = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* EU-27 plus Northern Ireland, which applies the EU rules on goods
     * (Windsor Framework) — marketplaces treat it as the EU for GPSR. */
    var EU = ['at', 'be', 'bg', 'cy', 'cz', 'de', 'dk', 'ee', 'es', 'fi', 'fr', 'gr', 'hr', 'hu', 'ie', 'it', 'lt', 'lu', 'lv', 'mt', 'nl', 'pl', 'pt', 'ro', 'se', 'si', 'sk'];
    var MARKETS = EU.concat(['xi']);

    /* Official languages a consumer label is written in, per market. The law
     * says «a language easily understood … as determined by the Member State»
     * (GPSR Art. 9(7)); for textiles «the official language(s)» (1007/2011
     * Art. 16(3)). Where a state has several, all are listed and marked:
     *   be — nl/fr/de by region; selling nationwide means all three;
     *   fi — Finnish and Swedish are both official;
     *   lu — fr or de (lb is not an EU language); both, to be safe;
     *   mt — Maltese and English are both official; English is accepted;
     *   ie, xi — English (Irish versions of older acts do not exist).
     * `any` = one of the list suffices, `all` = every one is expected. */
    var LANGS = {
        at: { all: ['de'] }, be: { all: ['nl', 'fr', 'de'] }, bg: { all: ['bg'] }, cy: { all: ['el'] },
        cz: { all: ['cs'] }, de: { all: ['de'] }, dk: { all: ['da'] }, ee: { all: ['et'] },
        es: { all: ['es'] }, fi: { all: ['fi', 'sv'] }, fr: { all: ['fr'] }, gr: { all: ['el'] },
        hr: { all: ['hr'] }, hu: { all: ['hu'] }, ie: { all: ['en'] }, it: { all: ['it'] },
        lt: { all: ['lt'] }, lu: { any: ['fr', 'de'] }, lv: { all: ['lv'] }, mt: { any: ['en', 'mt'] },
        nl: { all: ['nl'] }, pl: { all: ['pl'] }, pt: { all: ['pt'] }, ro: { all: ['ro'] },
        se: { all: ['sv'] }, si: { all: ['sl'] }, sk: { all: ['sk'] }, xi: { all: ['en'] },
    };

    /* The languages a label must carry for these markets, in a stable order
     * (the order of first appearance in the markets list). */
    function labelLanguages(markets) {
        var out = [];
        (markets || []).forEach(function (m) {
            var L = LANGS[m];
            if (!L) return;
            if (L.all) L.all.forEach(function (l) { if (out.indexOf(l) < 0) out.push(l); });
            else if (L.any && !L.any.some(function (l) { return out.indexOf(l) >= 0; })) out.push(L.any[0]);
        });
        return out;
    }

    /* Amazon's EU stores (the six with EPR checks since 2025 plus BE, IE, SE). */
    var PRESETS = {
        all: MARKETS.slice(),
        amazon: ['de', 'fr', 'it', 'es', 'nl', 'pl', 'se', 'be', 'ie'],
        big4: ['de', 'fr', 'it', 'es'],
        dach: ['de', 'at'],
    };

    var CATEGORIES = ['textile', 'toy', 'electronics', 'battery', 'cosmetics', 'candle', 'jewelry', 'furniture', 'paper', 'kitchen', 'other'];

    /* ── the card, read defensively ──────────────────────────────────────── */
    function str(v) { return v == null ? '' : String(v).trim(); }
    function facts(card) {
        card = card || {};
        var cat = CATEGORIES.indexOf(card.category) >= 0 ? card.category : 'other';
        var x = card.extras || {};
        var markets = (card.markets || []).filter(function (m) { return MARKETS.indexOf(m) >= 0; });
        var mfrCountry = str(card.mfr && card.mfr.country).toLowerCase();
        var mfrInEU = MARKETS.indexOf(mfrCountry) >= 0;
        var isToy = cat === 'toy';
        var isEEE = cat === 'electronics' || !!x.electrical;
        var radio = !!x.radio;
        var hasBattery = cat === 'battery' || !!x.batteries;
        return {
            cat: cat, markets: markets, mfrInEU: mfrInEU,
            isToy: isToy, isEEE: isEEE, radio: radio, hasBattery: hasBattery,
            batteryOnly: cat === 'battery',
            isTextile: cat === 'textile' || !!x.textile,
            isCosmetic: cat === 'cosmetics',
            isClp: cat === 'candle' || !!x.clp,
            isFoodContact: cat === 'kitchen' || !!x.foodContact,
            ce: isToy || isEEE || radio || cat === 'battery',
            sells: function (c) { return markets.indexOf(c) >= 0; },
        };
    }

    /* ── what is still empty, by field ───────────────────────────────────── */
    function missingMfr(card) {
        var m = card.mfr || {}, out = [];
        if (!str(m.name)) out.push('mfr.name');
        if (!str(m.address)) out.push('mfr.address');
        if (!str(m.email)) out.push('mfr.email');
        return out;
    }
    function missingRp(card) {
        var r = card.rp || {}, out = [];
        if (!str(r.name)) out.push('rp.name');
        if (!str(r.address)) out.push('rp.address');
        if (!str(r.email)) out.push('rp.email');
        return out;
    }
    function missingId(card) {
        return (str(card.type) || str(card.batch) || str(card.serial)) ? [] : ['id'];
    }
    function fibreSum(card) {
        return ((card.textile && card.textile.fibres) || []).reduce(function (a, f) { return a + (+f.pct || 0); }, 0);
    }

    /* ── the rules ───────────────────────────────────────────────────────── */
    /* `where`: product | pack | parcel | doc | listing | file | register.
     * `law` is not translated: it is the citation a seller hands to whoever
     * checks. Titles and explanations live in the page's i18n (r.<id>). */
    var RULES = [
        // GPSR — every consumer product, harmonised or not.
        { id: 'gpsr_rp', since: '2024-12-13', where: ['register', 'listing', 'product', 'pack', 'parcel', 'doc'],
            law: 'GPSR (EU) 2023/988 Art. 16; Reg. (EU) 2019/1020 Art. 4',
            when: function (F) { return !F.mfrInEU; }, need: missingRp },
        { id: 'gpsr_id', since: '2024-12-13', where: ['product', 'pack', 'doc'], law: 'GPSR Art. 9(5)', need: missingId },
        { id: 'gpsr_mfr', since: '2024-12-13', where: ['product', 'pack', 'doc'], law: 'GPSR Art. 9(6)', need: missingMfr },
        { id: 'gpsr_importer', since: '2024-12-13', where: ['product', 'pack', 'doc'], law: 'GPSR Art. 11(3)',
            when: function (F, card) { return !F.mfrInEU && (card.rp || {}).role === 'importer'; } },
        { id: 'gpsr_lang', since: '2024-12-13', where: ['product', 'pack', 'doc'], law: 'GPSR Art. 9(7), 11(4)' },
        { id: 'gpsr_listing', since: '2024-12-13', where: ['listing'], law: 'GPSR Art. 19; Art. 22(9)',
            need: function (card) { return missingMfr(card).concat(missingId(card)); } },
        { id: 'gpsr_techdoc', since: '2024-12-13', where: ['file'], law: 'GPSR Art. 9(2)–(3)' },
        { id: 'gpsr_contact', since: '2024-12-13', where: ['listing', 'doc'], law: 'GPSR Art. 9(11)' },
        { id: 'gpsr_accident', since: '2024-12-13', where: ['register'], law: 'GPSR Art. 20' },

        // CE — only where a harmonisation act provides for it.
        { id: 'ce_required', since: null, where: ['product', 'file'], law: 'Reg. (EC) 765/2008 Art. 30; Dir. 2009/48 (toys), 2014/35 (LVD), 2014/30 (EMC), 2011/65 (RoHS), 2014/53 (RED), Reg. 2023/1542 (batteries)',
            when: function (F) { return F.ce; } },
        { id: 'ce_forbidden', since: null, where: ['product'], law: 'Reg. (EC) 765/2008 Art. 30(2)',
            when: function (F) { return !F.ce; } },

        // Toys.
        { id: 'toy_warnings', since: null, until: '2030-07-31', where: ['product', 'pack', 'listing'], law: 'Dir. 2009/48/EC Art. 11, Annex V',
            when: function (F) { return F.isToy; },
            need: function (card) { var t = card.toy || {}; return (t.warnings && t.warnings.length) || t.under3 || t.none ? [] : ['toy.warnings']; } },
        { id: 'toy_2030', since: '2030-08-01', where: ['product', 'pack', 'file'], law: 'Reg. (EU) 2025/2509 (applies 01.08.2030, Art. 59; Art. 57 keeps earlier toys sellable)',
            when: function (F) { return F.isToy; } },

        // Textiles.
        { id: 'tex_fibres', since: null, where: ['product', 'listing'], law: 'Reg. (EU) 1007/2011 Art. 5, 9, 14, 16; Annex I',
            when: function (F) { return F.isTextile; },
            need: function (card) { return Math.abs(fibreSum(card) - 100) < 0.01 ? [] : ['textile.fibres']; } },
        { id: 'tex_animal', since: null, where: ['product', 'listing'], law: 'Reg. (EU) 1007/2011 Art. 12',
            when: function (F, card) { return F.isTextile && !!(card.textile && card.textile.animal); } },
        { id: 'tex_care', since: null, where: ['product'], law: '— (voluntary; GINETEX symbols are trade marks)',
            when: function (F) { return F.isTextile; } },

        // Electrical and electronic equipment.
        { id: 'weee_symbol', since: null, where: ['product', 'pack', 'doc'], law: 'Dir. 2012/19/EU Art. 14(4), 15(2), Annex IX; DE ElektroG §9',
            when: function (F) { return F.isEEE; } },
        { id: 'weee_register', since: null, where: ['register'], law: 'Dir. 2012/19/EU Art. 16–17',
            when: function (F) { return F.isEEE; } },
        { id: 'weee_de_number', since: null, where: ['listing', 'doc'], law: 'DE ElektroG §6(3)',
            when: function (F) { return F.isEEE && F.sells('de'); },
            need: function (card) { return str((card.epr || {}).de_weee) ? [] : ['epr.de_weee']; } },
        { id: 'usbc', since: '2024-12-28', where: ['pack', 'listing'], law: 'Dir. 2014/53/EU Art. 3(4), 3a(2), 10(8), Annex Ia Parts III–IV (as amended by Dir. (EU) 2022/2380); laptops from 28.04.2026',
            when: function (F) { return F.radio; },
            need: function (card) { var u = card.usbc || {}; return str(u.max) || str(u.min) ? [] : ['usbc.power']; } },
        { id: 'red_cyber', since: '2025-08-01', where: ['file'], law: 'Delegated Reg. (EU) 2022/30 (RED Art. 3(3)(d)(e)(f))',
            when: function (F) { return F.radio; } },

        // Batteries (sold alone or inside the product).
        { id: 'bat_symbol', since: '2025-08-18', where: ['product', 'pack'], law: 'Reg. (EU) 2023/1542 Art. 13(4), Annex VI Part B',
            when: function (F) { return F.hasBattery; } },
        { id: 'bat_cdpb', since: '2025-08-18', where: ['product'], law: 'Reg. (EU) 2023/1542 Art. 13(5)',
            when: function (F, card) { var b = card.battery || {}; return F.hasBattery && (b.cd || b.pb); } },
        { id: 'bat_ce', since: '2024-08-18', where: ['product', 'file'], law: 'Reg. (EU) 2023/1542 Art. 17–20',
            when: function (F) { return F.batteryOnly; } },
        { id: 'bat_label', since: '2026-08-18', pending: 'bat_act', where: ['product'], law: 'Reg. (EU) 2023/1542 Art. 13(1)–(3), (10)',
            when: function (F) { return F.hasBattery; } },
        { id: 'bat_qr', since: '2027-02-18', where: ['product'], law: 'Reg. (EU) 2023/1542 Art. 13(6)',
            when: function (F) { return F.hasBattery; } },
        { id: 'bat_removable', since: '2027-02-18', where: ['product'], law: 'Reg. (EU) 2023/1542 Art. 11',
            when: function (F) { return F.hasBattery && !F.batteryOnly; } },
        { id: 'bat_epr', since: null, where: ['register'], law: 'Reg. (EU) 2023/1542 Art. 55–56',
            when: function (F) { return F.hasBattery; } },

        // Cosmetics.
        { id: 'cos_rp', since: null, where: ['register', 'product', 'pack'], law: 'Reg. (EC) 1223/2009 Art. 4, 19(1)(a)',
            when: function (F) { return F.isCosmetic; } },
        { id: 'cos_cpnp', since: null, where: ['register'], law: 'Reg. (EC) 1223/2009 Art. 13',
            when: function (F) { return F.isCosmetic; } },
        { id: 'cos_safety', since: null, where: ['file'], law: 'Reg. (EC) 1223/2009 Art. 10–11',
            when: function (F) { return F.isCosmetic; } },
        { id: 'cos_label', since: null, where: ['product', 'pack'], law: 'Reg. (EC) 1223/2009 Art. 19',
            when: function (F) { return F.isCosmetic; },
            need: function (card) { var c = card.cosmetics || {}, o = []; if (!str(c.content)) o.push('cos.content'); if (!str(c.inci)) o.push('cos.inci'); if (!str(c.pao) && !str(c.bbd)) o.push('cos.durability'); return o; } },

        // Candles, wax melts, diffusers: a classified fragrance mixture.
        { id: 'clp_label', since: null, where: ['product', 'pack'], law: 'Reg. (EC) 1272/2008 Art. 17–33, Annex I 1.2.1',
            when: function (F) { return F.isClp; },
            need: function (card) { var c = card.clp || {}, o = []; if (!(c.codes && c.codes.length) && !c.notClassified) o.push('clp.codes'); if (!str(c.phone)) o.push('clp.phone'); return o; } },
        { id: 'clp_ufi', since: null, where: ['product', 'register'], law: 'Reg. (EC) 1272/2008 Art. 45, Annex VIII',
            when: function (F, card) { var c = card.clp || {}; return F.isClp && (c.codes || []).some(function (k) { return /^H[23]\d\d/.test(k); }); } },
        { id: 'candle_no_ce', since: null, where: ['product'], law: 'GPSR; EN 15494 (standard, not drawn here)',
            when: function (F) { return F.cat === 'candle'; } },

        // Jewellery.
        { id: 'jew_reach', since: null, where: ['file'], law: 'Reg. (EC) 1907/2006 Annex XVII entries 23 (Cd), 27 (Ni), 63 (Pb)',
            when: function (F) { return F.cat === 'jewelry'; } },

        // Food contact.
        { id: 'fcm_label', since: null, where: ['product', 'pack', 'file'], law: 'Reg. (EC) 1935/2004 Art. 15–16, Annex II',
            when: function (F) { return F.isFoodContact; } },

        // Packaging: every parcel to a consumer carries packaging.
        { id: 'pack_epr', since: null, where: ['register'], law: 'PPWR (EU) 2025/40 Art. 44–45; national EPR law', link: 'epr-check' },
        { id: 'ppwr_id', since: '2026-08-12', where: ['pack', 'doc'], law: 'PPWR (EU) 2025/40 Art. 15(5)–(6)',
            need: function (card) { return missingMfr(card).filter(function (k) { return k !== 'mfr.email'; }).concat(missingId(card)); } },
        { id: 'ppwr_label', since: '2028-08-12', pending: 'ppwr_act', where: ['pack'], law: 'PPWR (EU) 2025/40 Art. 12(1), 12(6), 12(12)' },
        { id: 'pack_fr', since: '2022-01-01', where: ['pack', 'product', 'doc'], law: 'FR Code de l’environnement L541-9-3, R541-12-17 (décret 2021-835)',
            when: function (F) { return F.sells('fr'); } },
        { id: 'pack_fr_idu', since: null, where: ['listing', 'doc'], law: 'FR Code de l’environnement L541-10-13, R541-173',
            when: function (F) { return F.sells('fr'); },
            need: function (card) { return str((card.epr || {}).fr_idu) ? [] : ['epr.fr_idu']; } },
        { id: 'pack_it', since: '2023-01-01', where: ['pack'], law: 'IT D.Lgs 152/2006 art. 219(5); Decision 97/129/EC',
            when: function (F) { return F.sells('it'); },
            need: function (card) { return ((card.packaging || {}).parts || []).length ? [] : ['pack.parts']; } },
        { id: 'pack_es', since: '2025-01-01', where: ['pack'], law: 'ES Real Decreto 1055/2022 art. 13',
            when: function (F) { return F.sells('es'); },
            need: function (card) { return ((card.packaging || {}).parts || []).length ? [] : ['pack.parts']; } },
        { id: 'pack_de', since: null, where: ['register', 'listing'], law: 'DE VerpackG §9 (LUCID), §7 (system participation)',
            when: function (F) { return F.sells('de'); },
            need: function (card) { return str((card.epr || {}).de_lucid) ? [] : ['epr.de_lucid']; } },
    ];

    /* What the pending acts are, for the «waits for» line. Checked in Cellar
     * (titles naming the parent act) on 21.10.2026: none adopted. */
    var PENDING = {
        bat_act: { checked: '2026-10-21', rule: '+18 months after the implementing act of Art. 13(10)' },
        ppwr_act: { checked: '2026-10-21', rule: '+24 months after the implementing acts of Art. 12(6)–(7)' },
    };

    function statusOf(rule, today) {
        if (rule.until && today > rule.until) return 'ended';
        if (rule.pending) return today >= rule.since ? 'pending' : 'pending';
        if (!rule.since || today >= rule.since) return 'now';
        return 'soon';
    }

    /* Which countries a rule is about: a country rule names its country,
     * the rest apply wherever the product is sold. */
    var COUNTRY_RULE = { weee_de_number: 'de', pack_fr: 'fr', pack_fr_idu: 'fr', pack_it: 'it', pack_es: 'es', pack_de: 'de' };

    function evaluate(card, today) {
        card = card || {};
        // «сегодня» — день человека (window.__localYmd), не гринвичский
        today = today || ((typeof window !== 'undefined' && window.__localYmd) ? window.__localYmd() : (function (d) {
            var m = d.getMonth() + 1, x = d.getDate();
            return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (x < 10 ? '0' : '') + x;
        })(new Date()));
        var F = facts(card);
        var items = [];
        RULES.forEach(function (r) {
            if (r.when && !r.when(F, card)) return;
            var st = statusOf(r, today);
            if (st === 'ended') return;
            var miss = r.need ? r.need(card) : [];
            items.push({
                id: r.id, status: st, since: r.since || null, until: r.until || null,
                pending: r.pending ? PENDING[r.pending] : null,
                where: r.where.slice(), law: r.law, country: COUNTRY_RULE[r.id] || null,
                missing: miss, link: r.link || null,
            });
        });
        // Order: what applies now and still lacks data, then now, then dated, then waiting.
        var rank = { now: 0, soon: 2, pending: 3 };
        items.sort(function (a, b) {
            var ra = rank[a.status] + (a.status === 'now' && !a.missing.length ? 1 : 0);
            var rb = rank[b.status] + (b.status === 'now' && !b.missing.length ? 1 : 0);
            if (ra !== rb) return ra - rb;
            return (a.since || '') < (b.since || '') ? -1 : (a.since || '') > (b.since || '') ? 1 : 0;
        });
        var counts = { now: 0, todo: 0, soon: 0, pending: 0 };
        items.forEach(function (i) {
            counts[i.status] = (counts[i.status] || 0) + 1;
            if (i.status === 'now' && i.missing.length) counts.todo++;
        });
        return {
            facts: { cat: F.cat, mfrInEU: F.mfrInEU, isToy: F.isToy, isEEE: F.isEEE, hasBattery: F.hasBattery, ce: F.ce, radio: F.radio, isTextile: F.isTextile, isCosmetic: F.isCosmetic, isClp: F.isClp, isFoodContact: F.isFoodContact },
            markets: F.markets,
            languages: labelLanguages(F.markets),
            items: items,
            counts: counts,
            symbols: symbolsFor(F, card),
        };
    }

    /* Which marks this product gets, and which it must NOT get. */
    function symbolsFor(F, card) {
        var on = [], off = [];
        if (F.ce) on.push('ce'); else off.push('ce');
        if (F.isEEE) on.push(card.weeeBar === false ? 'weee' : 'weeeBar');
        if (F.hasBattery && !F.isEEE) on.push('battery');
        if (F.isToy && card.toy && card.toy.under3 === 'pictogram') on.push('age03');
        if (F.isClp) ((card.clp || {}).pictos || []).forEach(function (p) { if (p === 'ghs07' || p === 'ghs09') on.push(p); });
        if (F.sells('fr')) on.push('triman');
        if (F.sells('es')) on.push('esbin');
        return { on: on, off: off };
    }

    /* Decision 97/129/EC (OJ L 50, 20.2.1997, Annexes I–VII): the only codes
     * the decision assigns. Composites take «C/» + the predominant material
     * (Annex VII footnote). 7–19 for plastics are NOT assigned by it. */
    var PACK_CODES = {
        PET: { n: 1, abbr: 'PET', fam: 'plastic' }, HDPE: { n: 2, abbr: 'HDPE', fam: 'plastic' }, PVC: { n: 3, abbr: 'PVC', fam: 'plastic' },
        LDPE: { n: 4, abbr: 'LDPE', fam: 'plastic' }, PP: { n: 5, abbr: 'PP', fam: 'plastic' }, PS: { n: 6, abbr: 'PS', fam: 'plastic' },
        PAP20: { n: 20, abbr: 'PAP', fam: 'paper' }, PAP21: { n: 21, abbr: 'PAP', fam: 'paper' }, PAP22: { n: 22, abbr: 'PAP', fam: 'paper' },
        FE: { n: 40, abbr: 'FE', fam: 'metal' }, ALU: { n: 41, abbr: 'ALU', fam: 'metal' },
        FOR50: { n: 50, abbr: 'FOR', fam: 'wood' }, FOR51: { n: 51, abbr: 'FOR', fam: 'wood' },
        TEX60: { n: 60, abbr: 'TEX', fam: 'textile' }, TEX61: { n: 61, abbr: 'TEX', fam: 'textile' },
        GL70: { n: 70, abbr: 'GL', fam: 'glass' }, GL71: { n: 71, abbr: 'GL', fam: 'glass' }, GL72: { n: 72, abbr: 'GL', fam: 'glass' },
        C80: { n: 80, abbr: 'C/PAP', fam: 'paper' }, C81: { n: 81, abbr: 'C/PAP', fam: 'paper' }, C82: { n: 82, abbr: 'C/PAP', fam: 'paper' },
        C84: { n: 84, abbr: 'C/PAP', fam: 'paper' }, C90: { n: 90, abbr: 'C/LDPE', fam: 'plastic' }, C91: { n: 91, abbr: 'C/LDPE', fam: 'plastic' },
        C92: { n: 92, abbr: 'C/LDPE', fam: 'plastic' }, C95: { n: 95, abbr: 'C/GL', fam: 'glass' },
    };
    function packCode(key) {
        var c = PACK_CODES[key];
        return c ? c.abbr + ' ' + c.n : null;
    }

    /* Where each material goes, per country, in that country's words.
     *   it — «Raccolta <famiglia>» + «Verifica le disposizioni del tuo
     *        Comune»: the formula of the MASE guidelines (DM 360/2022) as
     *        reported by CONAI and the trade press; the guideline PDF itself
     *        was not reachable on 21.10.2026 — editable on the page.
     *   es — the fraction/container (RD 1055/2022 art. 13.2); colours are
     *        the national system (amarillo = envases ligeros, azul = papel y
     *        cartón, verde = vidrio), also written as words.
     *   fr — destination per element; since the 2023 sorting extension all
     *        household packaging except glass goes to the sorting bin. */
    var DEST = {
        it: { plastic: 'Raccolta Plastica', paper: 'Raccolta Carta', metal: 'Raccolta Metalli', glass: 'Raccolta Vetro', wood: 'Raccolta Legno', textile: 'Raccolta Indifferenziata', tail: 'Verifica le disposizioni del tuo Comune' },
        es: { plastic: 'amarillo', paper: 'azul', metal: 'amarillo', glass: 'verde', wood: 'gris', textile: 'gris' },
        fr: { plastic: 'Bac de tri', paper: 'Bac de tri', metal: 'Bac de tri', glass: 'Conteneur à verre', wood: 'Déchèterie', textile: 'Point d’apport textile' },
    };
    function destination(country, key) {
        var c = PACK_CODES[key];
        var d = DEST[country];
        if (!c || !d) return null;
        return d[c.fam] || null;
    }

    return {
        EU: EU, MARKETS: MARKETS, LANGS: LANGS, PRESETS: PRESETS, CATEGORIES: CATEGORIES,
        RULES: RULES, PENDING: PENDING, PACK_CODES: PACK_CODES, DEST: DEST,
        labelLanguages: labelLanguages, facts: facts, evaluate: evaluate, symbolsFor: symbolsFor,
        packCode: packCode, destination: destination, fibreSum: fibreSum,
    };
});
